import { useButtonStore } from "../../stores/components/button";
import { useCrmStore } from "../../stores/crm";
import { Select } from "../ui/inputs/Select";
import { Input } from "../ui/inputs/Input";
import { useEffect } from "react";
import { getDepartments } from "../../api/departments";
import { showToastError, showToastSuccess } from "../../services/toast";
import {
  createMission,
  getMission,
  renewMission,
  updateMission,
} from "../../api/customerFile";
import { getBillingFrequencies } from "../../api/billingFrequencies";
import { formatComptableMaisPasTrop, formatDateForInput } from "../../services/function";
import { ToggleSwitch } from "../ui/buttons/ToggleSwitch";
import { getInvoicesTemplates } from "../../api/invoicesTemplates";
import { MonthYearInput } from "../ui/inputs/MonthlyPicker";
import { TextArea } from "../ui/inputs/TextArea";
import { IMission } from "../../interfaces/crm";
import { getSocialDiscount, getSocialStructure } from "../../api/social";
import { ActionButton, DetailHeader } from "../ui/page/PageShell";

const Missions = () => {
  const { setCreateButton } = useButtonStore();
  const {
    clientName,
    formData,
    setFormData,
    departments,
    setDepartments,
    setViewOrUpdate,
    idCustomer,
    billingFrequencies,
    setBillingFrequencies,
    viewOrUpdate,
    idMission,
    setIdMission,
    docModels,
    setDocModels,
    typeStructure,
    setTypeStructure,
    discount,
    setDiscount,
  } = useCrmStore();

  // Récupère les templates de documents, les départements et les fréquences de facturation
  useEffect(() => {
    getInvoicesTemplates()
      .then((res) => {
        const formattedData = res?.data?.map((data: any) => ({
          label: data.name,
          value: data.id,
        }));
        setDocModels(formattedData);
      })
      .catch(() => showToastError("Une erreur est survenue"));

    getDepartments()
      .then((res) => {
        const formattedData = res?.data?.map((data: any) => ({
          label: data.name,
          value: data.id,
        }));
        setDepartments(formattedData);
      })
      .catch(() => showToastError("Une erreur est survenue"));

    getBillingFrequencies()
      .then((res) => {
        const formattedData = res?.data?.map((data: any) => ({
          label: data.name,
          value: data.id,
        }));
        setBillingFrequencies(formattedData);
      })
      .catch(() => showToastError("Une erreur est survenue"));
  }, []);

  // Récupère les données du dossier client si on est en mode modification ou visualisation
  useEffect(() => {
    if (viewOrUpdate !== "" && idMission !== 0 && idCustomer !== 0) {
      getMission(idCustomer, idMission)
        .then((res) => {
          const data = res.data;

          setFormData({
            ...data,
            startDate: formatDateForInput(data.startDate),
            endDate: formatDateForInput(data.endDate),
            billingStartDate: formatDateForInput(data.billingStartDate),
            idStructureType: data.idStructureType || null,
            idDiscount: data.idDiscount || null,
          });
        })
        .catch(() => {
          showToastError("Une erreur est survenue");
        });
    }
  }, [viewOrUpdate]);

  // Call pour recupérer les types de structure + remise pour les clients SOCIAL
  useEffect(() => {
    if(formData.idDepartment === 3) {
      // Type de structure
      getSocialStructure()
        .then((res) => {
          const formattedData = res?.data?.map((data: any) => ({
            label: data.name,
            value: data.id,
          }));
          setTypeStructure(formattedData);
        })
        .catch(() => showToastError("Une erreur est survenue"));

      // Remise
      getSocialDiscount()
        .then((res) => {
          const formattedData = res?.data?.map((data: any) => ({
            label: data.name,
            value: data.id,
          }));
          setDiscount(formattedData);
        })
        .catch(() => showToastError("Une erreur est survenue"));
    }
  }, [formData])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };
  const handleChangeNumber = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value === "" ? 0 : parseFloat(e.target.value);
    if (!isNaN(value)) {
      setFormData({ ...formData, [e.target.name]: value });
    }
  };
  const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setFormData({ ...formData, [e.target.name]: parseInt(e.target.value) });
  };
  const handleDateChange = (date: string | null) => {
    setFormData({ ...formData, billingStartDate: date });
  };
  const handleChangeTextArea = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // Création ou modification de la mission en fonction de viewOrUpdate
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Convertir les valeurs en entiers avant l'envoi
    const dataToSend = {
      ...formData,
      budgetEuros: formData.budgetEuros ? Number(formData.budgetEuros.toString()) : 0,
      budgetTime: formData.budgetTime ? Number(formData.budgetTime.toString()) : 0,
      plannedTime: formData.plannedTime ? Number(formData.plannedTime.toString()) : 0,
    } as IMission;
    
    if (formData.idDepartment === 3) {
      dataToSend.periodicAmountExclVAT = undefined;
      dataToSend.billingStartDate = undefined;
    }

    if (viewOrUpdate === "") {
      createMission(idCustomer, dataToSend)
        .then(() => {
          showToastSuccess("La mission a bien été créée");
          setViewOrUpdate("");
          setFormData({});
          setCreateButton(false);
        })
        .catch(() => showToastError("Une erreur est survenue"));
    } else {
      updateMission(idCustomer, idMission, dataToSend)
        .then(() => {
          showToastSuccess("La mission a bien été modifiée");
          setViewOrUpdate("");
          setFormData({});
          setCreateButton(false);
        })
        .catch((error) => {
          if (
            error.response.data.message ===
            "You can not close a mission with times not validated"
          ) {
            showToastError(
              "Vous ne pouvez pas clôturer une mission avec des temps non validés"
            );
          } else {
            showToastError("Une erreur est survenue");
          }
        });
    }
  };

  const handleRenewMission = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    renewMission(idCustomer, idMission)
      .then(() => {
        showToastSuccess("La mission a bien été reconduite");
        setViewOrUpdate("");
        setFormData({});
        setCreateButton(false);
      })
      .catch((error) => {
        if (
          error.response.data.message ===
          "You can not renew a mission with times not validated, or already closed"
        ) {
          showToastError(
            "Vous ne pouvez pas clôturer une mission avec des temps non validés"
          );
        } else {
          showToastError("Une erreur est survenue");
        }
      });
  };

  useEffect(() => {
    if (formData.idBillingFrequency === 1) {
      const { periodicAmountExclVAT, billingStartDate, ...restFormData } =
        formData;
      setFormData(restFormData);
    }
  }, [formData.idBillingFrequency]);

  return (
    <div className="flex h-full min-h-0 w-full flex-col overflow-hidden">
      <DetailHeader
        title={
          viewOrUpdate === "view"
            ? `Modification pour ${clientName}`
            : `Création d'une mission pour ${clientName}`
        }
        hint="Merci de renseigner les informations de la mission"
        onClose={() => {
          setViewOrUpdate("");
          setFormData({});
          setCreateButton(false);
          setIdMission(0);
        }}
        actions={
          <ActionButton variant="primary" type="submit" form="missionForm">
            {viewOrUpdate !== "" ? "Modifier" : "Créer"}
          </ActionButton>
        }
      />

      <div className="flex min-h-0 w-full flex-1 items-start overflow-hidden p-3 sm:p-4">
        <form
          id="missionForm"
          className="flex h-full min-h-0 w-full gap-3 overflow-hidden"
          onSubmit={handleSubmit}
        >
          <div className="flex h-full min-h-0 w-2/3 flex-col items-start overflow-y-auto rounded-xl border border-slate-200 bg-white px-4 pb-4">
            <div className="sticky top-0 -mx-4 mb-3 flex w-[calc(100%+2rem)] flex-wrap items-center justify-between gap-2 border-b border-slate-100 bg-white px-4 py-2.5">
              <h4 className="text-[11px] font-bold tracking-wider text-slate-400 uppercase">
                Informations mission et facturation
              </h4>
              {viewOrUpdate !== "" ? (
                <ActionButton
                  onClick={(e) => {
                    handleRenewMission(e);
                  }}
                >
                  Reconduire
                </ActionButton>
              ) : null}
            </div>
            <div className="w-full flex justify-between items-center gap-4 mt-2">
              <Select
                label="Type de mission"
                name="idDepartment"
                value={formData.idDepartment || ""}
                onChange={handleSelectChange}
                fetchOptions={departments}
                className="2xl:w-2/3 w-full"
              />
              <Select
                label="Model de document"
                name="idInvoiceTemplate"
                value={formData.idInvoiceTemplate || ""}
                onChange={handleSelectChange}
                fetchOptions={docModels}
                className="2xl:w-2/3 w-full"
              />
            </div>
            <div className="w-full flex justify-start items-center gap-4 mt-4">
              <Input
                label="Date de début"
                name="startDate"
                value={formData.startDate || ""}
                onChange={handleChange}
                type="date"
              />
              <Input
                label="Date de fin"
                name="endDate"
                value={formData.endDate || ""}
                onChange={handleChange}
                type="date"
              />

          {formData.idDepartment === 3 && <>  
            <Select
                label="Type de structure"
                name="idStructureType"
                value={formData.idStructureType || ""}
                onChange={handleSelectChange}
                fetchOptions={typeStructure}
                className="w-full"
                required={formData.idDepartment === 3 ? true : false}
              />
              <Select
                label="Remise"
                name="idDiscount"
                value={formData.idDiscount || ""}
                onChange={handleSelectChange}
                fetchOptions={discount}
                className="w-2/3"
                required={formData.idDepartment === 3 ? true : false}
              />
              </>
              }
            </div>
            <div className="w-full flex justify-start items-center gap-4 mt-4">
              <Select
                label="Fréquence de facturation"
                name="idBillingFrequency"
                value={formData.idBillingFrequency || ""}
                onChange={handleSelectChange}
                fetchOptions={billingFrequencies}
                className="w-1/2"
              />

              {formData.idBillingFrequency !== 1 && formData.idDepartment !== 3 ? (
                <>
                  <MonthYearInput
                    label="Date de début de facturation"
                    name="billingStartDate"
                    value={formData.billingStartDate}
                    onChange={handleDateChange}
                    className="w-auto"
                  />
                  {/* Pas de montant HT pour les missions sociales */}
                  <Input
                    label="Montant HT"
                    name="periodicAmountExclVAT"
                    value={formData.periodicAmountExclVAT || ""}
                    onChange={handleChangeNumber}
                    type="number"
                  />
                </>
              ) : null}

            </div>
            <div className="w-full flex justify-between items-center gap-4 ">
              {formData.idBillingFrequency !== 1 && formData.idDepartment !== 3 ? (
                <div className="w-full flex flex-col items-start gap-2">
                  <TextArea
                    label="Libellé"
                    name="textPeriodicInvoice"
                    value={formData?.textPeriodicInvoice || ""}
                    onChange={handleChangeTextArea}
                    clasName="w-full pt-2 "
                  />
                  <p className="w-full italic text-xs flex justify-center items-center text-grey font-thin">
                    {`{{PERIOD}} : pour définir mois ou période de facturation.`}{" "}
                    {`{{YEAR}} : pour définir l'année de facturation`}
                  </p>
                </div>
              ) : null}
            </div>
            <Input
              label="Numéro de commande"
              name="orderNumber"
              clasName="mt-4"
              value={formData.orderNumber || ""}
              onChange={handleChange}
            />

            <ToggleSwitch
              label="Clôturer la mission"
              name="closed"
              value="closed"
              checked={formData.closed || false}
              onChange={(e) =>
                setFormData({ ...formData, closed: e.target.checked })
              }
              className="mt-4"
            />
          </div>
          <div className="h-full min-h-0 w-1/3 overflow-y-auto rounded-xl border border-slate-200 bg-white p-4">
            <h4 className="mb-3 text-[11px] font-bold tracking-wider text-slate-400 uppercase">
              Informations budget
            </h4>
            <Input
              label="Budget temps (en heures)"
              name="budgetTime"
              type="text"
              value={formData.budgetTime ?? ""}
              onChange={handleChange}
              clasName="w-3/4 mb-2"
            />
            {viewOrUpdate !== "" ? (
              <p className=" mb-4 text-grey 3xl:text-md text-sm font-thin">
                Temps passé : {formData.totalTimes}
              </p>
            ) : null}
            <Input
              label="Budget en euros "
              name="budgetEuros"
              type="text"
              value={formData.budgetEuros ?? ""}
              onChange={handleChange}
              clasName="w-3/4 mb-2"
            />
            {viewOrUpdate !== "" ? (
              <p className=" mb-4 text-grey 3xl:text-md text-sm font-thin">
                Temps validés : {formData.totalValidatedTimes}
              </p>
            ) : null}
            <Input
              label="Temps plannifiés (en heures)"
              name="plannedTime"
              type="text"
              value={formData.plannedTime ?? ""}
              onChange={handleChange}
              clasName="w-3/4 mb-2"
            />
            {viewOrUpdate !== "" ? (
              <p className=" mb-4 text-grey 3xl:text-md text-sm font-thin">
                Temps valorisés : {formData.totalAmountTimes}
              </p>
            ) : null}

            <p className="mb-4 text-grey 3xl:text-md text-sm font-thin">
              Total facturé HT : {formatComptableMaisPasTrop(formData.totalGross) + " €"}
            </p>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Missions;
